import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Team } from '@/app/types/team';
import { listTeams, deleteTeam } from '@/app/services/teamService';
import TeamForm from './components/TeamForm';

const TeamsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentTeam, setCurrentTeam] = useState<Team | null>(null);
  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['teams', { include_inactive: true }],
    queryFn: () => listTeams({ include_inactive: true, limit: 200 }),
  });
  const teams = data?.items ?? [];

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteTeam(id),
    onSuccess: () => {
      toast.success('Team deactivated');
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      setTeamToDelete(null);
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail ?? 'Failed to delete team'),
  });

  const handleCreate = () => {
    setCurrentTeam(null);
    setIsFormOpen(true);
  };

  const handleEdit = (team: Team) => {
    setCurrentTeam(team);
    setIsFormOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Users className="h-6 w-6" /> Teams
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Group staff into teams / departments. Tasks assigned to a team can be picked up by any member.
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" /> New Team
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Lead</TableHead>
              <TableHead>Members</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  Loading teams…
                </TableCell>
              </TableRow>
            ) : teams.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  No teams yet. Create your first team to start routing tasks.
                </TableCell>
              </TableRow>
            ) : (
              teams.map((team) => (
                <TableRow key={team.id}>
                  <TableCell className="font-medium">{team.name}</TableCell>
                  <TableCell className="text-muted-foreground max-w-[260px] truncate">
                    {team.description || '—'}
                  </TableCell>
                  <TableCell>{team.team_lead?.full_name || team.team_lead?.username || '—'}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {team.members.slice(0, 3).map((m) => (
                        <Badge key={m.id} variant="secondary">
                          {m.full_name || m.username}
                        </Badge>
                      ))}
                      {team.member_count > 3 && (
                        <Badge variant="outline">+{team.member_count - 3}</Badge>
                      )}
                      {team.member_count === 0 && <span className="text-muted-foreground">—</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={team.is_active ? 'default' : 'outline'}>
                      {team.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(team)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {team.is_active && (
                      <Button variant="ghost" size="icon" onClick={() => setTeamToDelete(team)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <TeamForm open={isFormOpen} onOpenChange={setIsFormOpen} team={currentTeam} />

      <AlertDialog open={!!teamToDelete} onOpenChange={(o) => !o && setTeamToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate team "{teamToDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              The team will be deactivated and hidden from selectors. Existing tasks and subscriptions
              that reference it keep their link for reporting. You can reactivate it later by editing it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => teamToDelete && deleteMutation.mutate(teamToDelete.id)}>
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TeamsPage;
